import config from '../../config';

import { useProjectStore } from '../../stores/project';

import { Event } from '../../types';
import { isEmpty } from '../../utils';

import { getAccessTokenClient, getApiKeyClient } from '../client/http';

const {
  apiBaseUrl: { elements: baseUrl },
} = config;

const accessTokenClient = () => getAccessTokenClient(baseUrl);
const apiKeyClient = () => getApiKeyClient(baseUrl);

export const createEvent = async (data: Partial<Event>) => {
  try {
    const response = await accessTokenClient().post('api/event', data);
    return response.data.event;
  } catch (err: any) {
    throw new Error('Error creating project', err);
  }
};

export const updateEvent = async (eventId: string, data: any) => {
  try {
    const formData = new FormData();

    formData.append('eventId', eventId);

    Object.keys(data).forEach((k: string) => {
      const key = k as keyof Event;
      const value: any = data[key];

      if (typeof value === 'string') formData.append(key, value);
      else if (Array.isArray(value))
        if (isEmpty(value)) formData.append(`${key}[]`, '');
        else value.forEach((a) => formData.append(`${key}[]`, a));
      else if (
        [
          'payment',
          'emailsData',
          'assigneeData',
          'attendeeReviewData',
        ].includes(key)
      )
        formData.append(key, JSON.stringify(value));
    });

    if (!!data.banner) {
      formData.append('hasCustomBanner', 'true');
      formData.append('banner', data.banner);
    }

    if (!!data.logo) {
      formData.append('hasCustomLogo', 'true');
      formData.append('logo', data.logo);
    }

    const response = await accessTokenClient().put('api/event', formData);

    return response.data.event;
  } catch (err: any) {
    throw new Error('Error updating project', err);
  }
};

export const deleteEvent = async (eventId: string) => {
  try {
    const response = await accessTokenClient().delete(`api/events/${eventId}`);
    return response.status;
  } catch (err: any) {
    throw new Error('Error deleting event', err);
  }
};

export const setEventMode = async (data: any, isPublic = false) => {
  try {
    await (isPublic ? apiKeyClient : accessTokenClient)().put(
      'api/event/videoMode',
      data,
    );
    return { success: true };
  } catch (err: any) {
    throw new Error('Error setting event video mode', err);
  }
};

export const getUserEvents = async (userId: string) => {
  try {
    const response = await accessTokenClient().get(
      `api/events?userId=${userId}&sort=latest`,
    );

    const userEvents = response.data.events;

    useProjectStore.setState((state: any) => ({ ...state, userEvents }));
    return userEvents;
  } catch (err: any) {
    throw new Error('Error getting user events', err);
  }
};

export const getEvent = async (eventId: string, isPublic?: boolean) => {
  try {
    const client = isPublic ? apiKeyClient : accessTokenClient;
    const response = await client().get(`api/events/${eventId}`);
    return response.data.event;
  } catch (err: any) {
    if (err.response?.status === 404) return null;
    else throw new Error('Error getting event', err);
  }
};

export const getEventPublic = async (eventId: string) => {
  if (!eventId) return null;

  try {
    const response = await apiKeyClient().get(`api/events/${eventId}`);
    const event = response.data.event;

    const returnEvent = {
      eventId: event.eventId,
      name: event.name,
      type: event.type,
      stage: event.stage,
      banner: event.banner,
      logo: event.logo,
      videoMode: event.videoMode,
      caption: event.caption || '',
      hashTag: event.hashTag,
      tsAndCsLink: event.tsAndCsLink,
      organiserName: event.organiserName,
      customFieldLabel: event.customFieldLabel,
      wildcards: event.wildcards,
      startDate: event.startDate,
      endDate: event.endDate,
      created: event.created,
      modified: event.modified,
      payment: {
        paid: event.payment?.paid ?? false,
        allowDonations: event.payment?.allowDonations ?? false,
      },
      attendeeReviewData: event.attendeeReviewData,
      assigneeData: event.assigneeData,
    };

    return returnEvent;
  } catch (err: any) {
    if (err.response?.status === 404) return null;
    else throw new Error('Error getting event', err);
  }
};

// getEventName is used in public Share Hub and Share Video pages
export const getEventName = async (eventId: string) => {
  try {
    const response = await apiKeyClient().get(`api/events/${eventId}`);
    return response.data.event.name;
  } catch (err: any) {
    throw new Error('Error getting event', err);
  }
};

export const getEventTypes = async () => {
  try {
    const response = await accessTokenClient().get('api/event-types');
    return response.data;
  } catch (err: any) {
    throw new Error('Error getting event types', err);
  }
};

export const getAnalysisStatus = async (props: {
  eventId: string;
  elementId?: string;
  isPublic?: boolean;
}) => {
  const { eventId, elementId, isPublic } = props;

  try {
    const client = isPublic ? apiKeyClient : accessTokenClient;
    const response = await client().get(
      `api/events/${eventId}/analysis-status${elementId ? `/${elementId}` : ''}`,
    );
    return response.data;
  } catch (err: any) {
    throw new Error('Error getting analysis status', err);
  }
};
